/**
 * Dependencies
 */
import {
    get,
    sumBy,
    isEmpty
} from 'lodash'

/**
 * Retrieve the primary project options list
 *
 * @param  {Object} state
 * @return {Array}
 */
const primaryOptions = (state) => {
    return Object.keys(state.projectChoices.options)
        .map((item) => {
            return {
                label: state.projectChoices.options[item].title,
                key: state.projectChoices.options[item].key,
                disabled: state.projectChoices.options[item].disabled
            }
        })
}

/**
 * Retrieve the secondary project options list
 *
 * @param  {Object} state
 * @return {Array}
 */
const secondaryOptions = (state) => {
    if (!state.projectChoices.selected.primary) return []
    let base = state.projectChoices.options[state.projectChoices.selected.primary].options
    return Object.keys(base)
        .map((item) => {
            return {
                label: base[item].title,
                key: base[item].key,
                disabled: base[item].disabled
            }
        })
}

/**
 * Retrieve the tertiary project options list
 *
 * @param  {Object} state
 * @return {Array}
 */
const tertiaryOptions = (state) => {
    if (!state.projectChoices.selected.primary || !state.projectChoices.selected.secondary) return []
    let base = state.projectChoices.options[state.projectChoices.selected.primary].options[state.projectChoices.selected.secondary].options
    return Object.keys(base).map((item) => {
        return {
            label: base[item].title,
            type: base[item].form_type,
            disabled: base[item].disabled
        }
    })
}

/**
 * Retrieve the selected options object
 *
 * @param  {Object} state
 * @return {Object}
 */
const selectedOptions = (state) => state.projectChoices.selected

/**
 * Determine whether the project contains errors
 *
 * @param  {Object} state
 * @return {Boolean}
 */
const hasErrors = (state) => !isEmpty(state.errors)

/**
 * Return an array of CAPEX type strings
 *
 * @param  {Object} state
 * @return {Array}
 */
const capexTypes = (state) => {
    if (state.data.capex) {
        return Object.keys(state.data.capex)
    }
    return []
}

/**
 * Return the total CAPEX Costs Value
 *
 * @param  {Object} state
 * @return {Number}
 */
const totalCapexCosts = (state, getters) => {
    if (state.data.capex) {
        let total = 0
        let capexItems = Object.keys(state.data.capex).filter(item => item !== 'WorkingCapitalSerializer')
        capexItems.forEach(set => {
            total += sumBy(state.data.capex[set], (item) => {
                // if (item.relativity.is_relative) {
                //     let relativeTotal = sumBy(item.relativity.relative_to, 'amount')
                //     return relativeTotal * (item.amount / 100)
                // }
                if (item.amount_unit === 2) {
                    return (item.amount || 0) * getters.unitTotal
                }
                return item.amount_unit === 1
                    ? item.amount * (state.data.project.building_sqft || 0)
                    : item.amount || 0
            })
        })
        total += state.data.capex['WorkingCapitalSerializer']
            ? state.data.capex['WorkingCapitalSerializer'].amount || 0
            : 0
        return total
    }
    return 0
}

/**
 * Return only the CAPEX items that are repeatables and should be iterated upon
 *
 * @param  {Object} state
 * @return {Array}
 */
const qualifiedCapexItems = (state) => {
    if (state.data.capex) {
        const capex = [
            {
                key: 'AcquisitionCostSerializer',
                label: 'Acquisition Costs',
                items: state.data.capex.AcquisitionCostSerializer
            },
            {
                key: 'SoftCostSerializer',
                label: 'Soft Costs',
                items: state.data.capex.SoftCostSerializer
            },
            {
                key: 'HardCostSerializer',
                label: 'Hard Costs',
                items: state.data.capex.HardCostSerializer
            }
        ]

        return capex.filter(item => item.items !== undefined)
    }
    return []
}

/**
 * Get the row total for each of the CAPEX categories
 *
 * @param  {Object} state
 * @return {Number}
 */
const totalCostsCount = (state) => {
    if (state.data.capex) {
        let total = 0
        let fields = Object
            .keys(state.data.capex)
            .filter(item => item !== 'WorkingCapitalSerializer')

        fields.forEach(set => {
            total += state.data.capex[set].length
        })

        return total
    }

    return 0
}

/**
 * Get the calculated year count based on the no_months input on the project tab
 *
 * @param  {Object} state
 * @return {Number}
 */
const totalYears = (state) => {
    return Math.ceil(get(state.data, 'project.no_months', 0) / 12)
}

/**
 * Check if the draft state object is empty
 *
 * @param  {Object} state
 * @return {Boolean}
 */
const hasDraft = (state) => !isEmpty(state.draft)

/**
 * Get the draft leaf if applicable
 *
 * @param  {Object} state
 * @return {String|Null}
 */
const draftLeaf = (state) => get(state.draft, 'project.leaf', null)

/**
 * Get the sum of the units
 *
 * @param  {Object} state
 * @return {Number}
 */
const unitTotal = state => {
    let units = get(state.data.unit, state.meta.unit_path, [])

    return units.reduce((sum, i) => {
        sum += i.quantity || i.quant
        return sum
    }, 0)
}

export default {
    hasDraft,
    draftLeaf,
    hasErrors,
    unitTotal,
    capexTypes,
    totalYears,
    primaryOptions,
    secondaryOptions,
    tertiaryOptions,
    selectedOptions,
    totalCapexCosts,
    totalCostsCount,
    qualifiedCapexItems
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/form/getters.js